import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Eye, EyeOff, Mail, Lock, User, Phone, ArrowRight, Loader2, 
  ArrowLeft, Shield, CheckSquare, Square, Globe, KeyRound
} from 'lucide-react';

const COLORS = {
  primary: '#1A237E',
  primaryLight: '#3949AB',
  primaryDark: '#0D1642',
  success: '#4CAF50',
  successLight: '#81C784',
  danger: '#F44336',
  warning: '#F9A825',
  white: '#FFFFFF',
  background: '#F5F5F5',
  cardBackground: '#FFFFFF',
  text: '#212121',
  textLight: '#757575',
  gray: '#9E9E9E',
  grayLight: '#E0E0E0',
};

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirectTo = searchParams.get('redirect') || '/';
  const initialMode = searchParams.get('mode') || 'login';
  
  const [step, setStep] = useState('form'); // 'form', 'verify-register', 'verify-login'
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    otp: '',
    language: 'english',
  });

  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  const languages = [
    { code: 'english', label: 'English' },
    { code: 'hindi', label: 'Hindi' },
    { code: 'tamil', label: 'Tamil' },
    { code: 'telugu', label: 'Telugu' },
    { code: 'kannada', label: 'Kannada' },
    { code: 'malayalam', label: 'Malayalam' },
    { code: 'marathi', label: 'Marathi' },
    { code: 'bengali', label: 'Bengali' },
    { code: 'gujarati', label: 'Gujarati' },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const storeUserAndRedirect = (data) => {
    localStorage.setItem('user', JSON.stringify(data.user));
    if (data.token) localStorage.setItem('token', data.token);
    setSuccess('Login successful! Redirecting...');
    const role = data.user?.role;
    setTimeout(() => navigate('/nurse-dashboard'), 1200);
  };

  // ---- LOGIN: Send OTP ----
  const handleLoginSendOTP = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) { setError('Please enter your email.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/admin/login-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess('OTP sent to your email!');
        setStep('verify-login');
      } else {
        setError(data.detail || 'Failed to send OTP. Are you registered?');
      }
    } catch (err) { console.error('Login OTP send failed:', err); setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  // ---- LOGIN: Verify OTP ----
  const handleLoginVerifyOTP = async (e) => {
    e.preventDefault();
    if (!formData.otp.trim()) { setError('Please enter the OTP.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/admin/verify-login-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, otp: formData.otp }),
      });
      const data = await res.json();
      if (res.ok) { storeUserAndRedirect(data); }
      else { setError(data.detail || 'Invalid OTP.'); }
    } catch (err) { console.error('Login OTP verify failed:', err); setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  // ---- REGISTER: Send OTP ----
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!privacyConsent || !termsConsent) { setError('Please accept Privacy Policy and Terms.'); return; }
    if (!formData.name.trim() || !formData.email.trim() || !formData.mobile.trim()) { setError('Please fill all required fields.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/admin/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name, email: formData.email, mobile: formData.mobile, language: formData.language }),
      });
      const data = await res.json();
      if (res.ok) { setSuccess('OTP sent! Please verify.'); setStep('verify-register'); }
      else { setError(data.detail || 'Registration failed.'); }
    } catch (err) { console.error('Register failed:', err); setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  // ---- REGISTER: Verify OTP ----
  const handleRegisterVerifyOTP = async (e) => {
    e.preventDefault();
    if (!formData.otp.trim()) { setError('Please enter the OTP.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/admin/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, otp: formData.otp }),
      });
      const data = await res.json();
      if (res.ok) {
        // Record consent
        if (data.user?.id) {
          try {
            await fetch(`${API_BASE_URL}/api/consent/record`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ user_id: data.user.id, consent_type: 'privacy', action: 'granted', version: '1.0' }),
            });
            await fetch(`${API_BASE_URL}/api/consent/record`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ user_id: data.user.id, consent_type: 'terms', action: 'granted', version: '1.0' }),
            });
          } catch (err) { console.error('Consent recording failed:', err); }
        }
        storeUserAndRedirect(data);
      } else { setError(data.detail || 'Invalid OTP.'); }
    } catch (err) { console.error('Register OTP verify failed:', err); setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const endpoint = step === 'verify-login' ? '/api/auth/admin/login-otp' : '/api/auth/admin/register';
      const body = step === 'verify-login' 
        ? { email: formData.email }
        : { name: formData.name, email: formData.email, mobile: formData.mobile };
      await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      setSuccess('OTP resent!');
    } catch (err) { console.error('OTP resend failed:', err); setError('Failed to resend OTP.'); }
    finally { setLoading(false); }
  };

  const renderInput = (Icon, label, inputProps) => (
    <div className="mb-5">
      <label className="block text-sm font-medium mb-2" style={{ color: COLORS.text }}>{label}</label>
      <div className="relative">
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: COLORS.gray }} size={20} />
        <input
          {...inputProps}
          onChange={handleChange}
          className="w-full pl-12 pr-4 py-4 rounded-xl outline-none transition-all"
          style={{ backgroundColor: COLORS.background, border: `2px solid ${COLORS.grayLight}`, color: COLORS.text }}
          onFocus={(e) => e.target.style.borderColor = COLORS.primary}
          onBlur={(e) => e.target.style.borderColor = COLORS.grayLight}
        />
      </div>
    </div>
  );

  const renderAlert = (type, message) => message ? (
    <div className="mb-6 p-4 rounded-xl text-sm" data-testid={`auth-${type}-message`}
      style={{ 
        backgroundColor: type === 'error' ? `${COLORS.danger}10` : `${COLORS.success}10`, 
        color: type === 'error' ? COLORS.danger : COLORS.success, 
        border: `1px solid ${type === 'error' ? COLORS.danger : COLORS.success}30` 
      }}>
      {message}
    </div>
  ) : null;

  // ===== OTP VERIFICATION VIEW =====
  const renderOTPView = (onSubmit) => (
    <div className="p-8">
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${COLORS.primary}15` }}>
          <KeyRound style={{ color: COLORS.primary }} size={40} />
        </div>
        <h1 className="text-2xl font-bold mb-2" style={{ color: COLORS.text }} data-testid="otp-title">Verify Your Email</h1>
        <p style={{ color: COLORS.textLight }}>
          Enter the 6-digit OTP sent to <span style={{ color: COLORS.primary }} className="font-medium">{formData.email}</span>
        </p>
      </div>
      {renderAlert('error', error)}
      {renderAlert('success', success)}
      <form onSubmit={onSubmit}>
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2" style={{ color: COLORS.text }}>Enter OTP</label>
          <input
            type="text" name="otp" value={formData.otp} onChange={handleChange}
            placeholder="Enter 6-digit OTP" maxLength={6} data-testid="otp-input"
            className="w-full px-4 py-4 rounded-xl text-center text-2xl tracking-[0.5em] font-mono outline-none transition-all"
            style={{ backgroundColor: COLORS.background, border: `2px solid ${COLORS.grayLight}`, color: COLORS.text }}
            onFocus={(e) => e.target.style.borderColor = COLORS.primary}
            onBlur={(e) => e.target.style.borderColor = COLORS.grayLight}
          />
        </div>
        <button type="submit" disabled={loading} data-testid="verify-otp-btn"
          className="w-full py-4 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: COLORS.primary }}>
          {loading ? <><Loader2 className="animate-spin" size={20} /> Verifying...</> : <>Verify & Continue <ArrowRight size={20} /></>}
        </button>
        <button type="button" onClick={handleResendOTP} disabled={loading} data-testid="resend-otp-btn"
          className="w-full mt-4 py-3 rounded-xl font-medium transition-all hover:bg-gray-100"
          style={{ border: `2px solid ${COLORS.grayLight}`, color: COLORS.textLight }}>
          Resend OTP
        </button>
        <button type="button" onClick={() => { setStep('form'); setError(null); setSuccess(null); setFormData(p => ({ ...p, otp: '' })); }}
          className="w-full mt-4 py-3 transition-colors" style={{ color: COLORS.gray }} data-testid="back-to-form-btn">
          Back
        </button>
      </form>
    </div>
  );

  // ===== MAIN RENDER =====
  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.background }}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 shadow-md" style={{ backgroundColor: COLORS.primary }}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="auth-logo-link">
            <img src="https://customer-assets.emergentagent.com/job_health-ghost/artifacts/h0ankv1n_logo.png" alt="Logo" className="h-10" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-white hover:text-gray-200 transition-colors" data-testid="auth-back-link">
            <ArrowLeft size={20} /><span className="font-medium">Back to Home</span>
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-20 px-4">
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="auth-card">
            
            {/* OTP Views */}
            {step === 'verify-login' && renderOTPView(handleLoginVerifyOTP)}
            {step === 'verify-register' && renderOTPView(handleRegisterVerifyOTP)}

            {/* Main Form View */}
            {step === 'form' && (
              <>
                {/* Tabs */}
                <div className="flex" style={{ borderBottom: `1px solid ${COLORS.grayLight}` }}>
                  {['login', 'signup'].map(m => (
                    <button key={m} onClick={() => { setMode(m); setError(null); setSuccess(null); }}
                      className="flex-1 py-5 text-center font-semibold text-lg transition-all"
                      data-testid={`auth-tab-${m}`}
                      style={{ 
                        color: mode === m ? COLORS.primary : COLORS.gray,
                        borderBottom: mode === m ? `3px solid ${COLORS.primary}` : 'none',
                        backgroundColor: mode === m ? `${COLORS.primary}08` : 'transparent'
                      }}>
                      {m === 'login' ? 'Login' : 'Register'}
                    </button>
                  ))}
                </div>

                <div className="p-8">
                  <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold mb-2" style={{ color: COLORS.text }} data-testid="auth-title">
                      {mode === 'login' ? 'Welcome Back' : 'Family Admin Registration'}
                    </h1>
                    <p style={{ color: COLORS.textLight }}>
                      {mode === 'login' ? "Sign in to manage your family's health" : 'Register as a Family Admin to manage medicine schedules'}
                    </p>
                  </div>

                  {renderAlert('error', error)}
                  {renderAlert('success', success)}

                  {/* LOGIN FORM */}
                  {mode === 'login' && (
                    <form onSubmit={handleLoginSendOTP} data-testid="login-form">
                      {renderInput(Mail, 'Email Address', { type: 'email', name: 'email', value: formData.email, placeholder: 'you@example.com', required: true, 'data-testid': 'login-email-input' })}
                      <button type="submit" disabled={loading} data-testid="login-submit-btn"
                        className="w-full py-4 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: COLORS.primary }}>
                        {loading ? <><Loader2 className="animate-spin" size={20} /> Sending OTP...</> : <>Send OTP <ArrowRight size={20} /></>}
                      </button>
                    </form>
                  )}

                  {/* REGISTER FORM */}
                  {mode === 'signup' && (
                    <form onSubmit={handleRegister} data-testid="register-form">
                      {renderInput(User, 'Full Name *', { type: 'text', name: 'name', value: formData.name, placeholder: 'Enter your full name', required: true })}
                      {renderInput(Phone, 'Mobile Number *', { type: 'tel', name: 'mobile', value: formData.mobile, placeholder: '+91 98765 43210', required: true })}
                      {renderInput(Mail, 'Email Address *', { type: 'email', name: 'email', value: formData.email, placeholder: 'Enter email for verification', required: true })}
                      
                      <div className="mb-6">
                        <label className="block text-sm font-medium mb-2" style={{ color: COLORS.text }}>Preferred Language</label>
                        <div className="relative">
                          <Globe className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: COLORS.gray }} size={20} />
                          <select name="language" value={formData.language} onChange={handleChange}
                            className="w-full pl-12 pr-4 py-4 rounded-xl outline-none transition-all appearance-none cursor-pointer"
                            style={{ backgroundColor: COLORS.background, border: `2px solid ${COLORS.grayLight}`, color: COLORS.text }}
                            data-testid="register-language-select">
                            {languages.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Consent */}
                      <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: COLORS.background, border: `1px solid ${COLORS.grayLight}` }}>
                        <h3 className="text-sm font-semibold mb-4" style={{ color: COLORS.text }}>Consent (Required)</h3>
                        {[
                          { state: privacyConsent, set: setPrivacyConsent, label: 'Privacy Policy', link: '/privacy', testId: 'privacy-consent-btn' },
                          { state: termsConsent, set: setTermsConsent, label: 'Terms of Service', link: '/terms', testId: 'terms-consent-btn' },
                        ].map(c => (
                          <button key={c.testId} type="button" onClick={() => c.set(!c.state)}
                            className="flex items-start gap-3 w-full text-left mb-3 group" data-testid={c.testId}>
                            {c.state 
                              ? <CheckSquare style={{ color: COLORS.success }} className="flex-shrink-0 mt-0.5" size={24} />
                              : <Square style={{ color: COLORS.gray }} className="flex-shrink-0 mt-0.5" size={24} />}
                            <span className="text-sm" style={{ color: COLORS.textLight }}>
                              I agree to the <Link to={c.link} style={{ color: COLORS.primary }} className="font-medium hover:underline">{c.label}</Link>
                            </span>
                          </button>
                        ))}
                        <div className="flex items-center gap-2 p-3 rounded-lg" style={{ backgroundColor: `${COLORS.success}15`, border: `1px solid ${COLORS.success}30` }}>
                          <Shield style={{ color: COLORS.success }} size={18} />
                          <span className="text-sm font-medium" style={{ color: COLORS.success }}>DPDP Act 2023 Compliant</span>
                        </div>
                      </div>

                      <button type="submit" disabled={loading || !privacyConsent || !termsConsent} data-testid="register-submit-btn"
                        className="w-full py-4 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: COLORS.primary }}>
                        {loading ? <><Loader2 className="animate-spin" size={20} /> Registering...</> : <>Register & Get OTP <ArrowRight size={20} /></>}
                      </button>
                    </form>
                  )}

                  {/* Divider & Toggle */}
                  <div className="my-6 flex items-center">
                    <div className="flex-1" style={{ borderTop: `1px solid ${COLORS.grayLight}` }} />
                    <span className="px-4 text-sm" style={{ color: COLORS.gray }}>or</span>
                    <div className="flex-1" style={{ borderTop: `1px solid ${COLORS.grayLight}` }} />
                  </div>
                  <div className="text-center text-sm" style={{ color: COLORS.textLight }}>
                    {mode === 'login' ? (
                      <p>Don{"'"}t have an account? <button onClick={() => setMode('signup')} className="font-medium hover:underline" style={{ color: COLORS.primary }} data-testid="switch-to-register">Register as Family Admin</button></p>
                    ) : (
                      <p>Already have an account? <button onClick={() => setMode('login')} className="font-medium hover:underline" style={{ color: COLORS.primary }} data-testid="switch-to-login">Sign in</button></p>
                    )}
                  </div>
                  <div className="mt-6 text-center">
                    <Link to="/pricing" className="text-sm hover:underline" style={{ color: COLORS.gray }} data-testid="voucher-link">
                      Have a voucher code? <span style={{ color: COLORS.primary }} className="font-medium">Activate here</span>
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>

          <p className="mt-6 text-center text-xs" style={{ color: COLORS.gray }}>
            By continuing, you agree to our <Link to="/terms" className="hover:underline" style={{ color: COLORS.primary }}>Terms</Link> and <Link to="/privacy" className="hover:underline" style={{ color: COLORS.primary }}>Privacy Policy</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default AuthPage;
